import type { FC } from "react";

import type { SelectOptionType } from "@ui/components/DEPRECATED_CustomSelect";
import { Controller, useFormContext } from "@ui/components/Form";
import { Select, TextInput } from "@ui/components/Inputs";
import { useTranslations } from "@ui/providers/useTranslations";

import type { IGetOrderDeliveryLimits } from "@jti/magento";

type CityFieldProps = {
  fieldName: (name: string) => string;
  isCityLimitationEnabled: boolean;
  deliveryLimitCitiesDropdownList: SelectOptionType[];
  orderDeliveryLimits?: IGetOrderDeliveryLimits;
};

const CITY_NAME = "city";

export const CityField: FC<CityFieldProps> = ({
  fieldName: getFieldName,
  isCityLimitationEnabled,
  deliveryLimitCitiesDropdownList,
  orderDeliveryLimits,
}) => {
  const { __ } = useTranslations();
  const { control } = useFormContext();
  const fieldName = getFieldName(CITY_NAME);

  const validateCity = (city: string | undefined): true | string => {
    if (!isCityLimitationEnabled) {
      return true;
    }

    const isValid = orderDeliveryLimits?.limits.some(({ name }) => name === city);

    return isValid || __("address_fields.city.error.delivery_limit");
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ required: __("form_validation.field_required"), validate: { validateCity } }}
      render={({ field, fieldState: { error } }) => {
        const commonSettings = {
          "data-testid": "city",
          name: fieldName,
          "aria-label": "city",
          isError: !!error,
          isHelperTextVisible: !!error,
          helperText: error?.message,
          required: true,
          label: __(`address_fields.city`),
          placeholder: __("address_fields.city_placeholder"),
          labelId: "label-address-city",
          autoComplete: "address-level2",
        };

        return isCityLimitationEnabled ? (
          <Select
            {...field}
            options={deliveryLimitCitiesDropdownList}
            onChange={(selectedOption) => field.onChange(selectedOption?.value)}
            value={field.value ? { label: field.value, value: field.value } : null}
            showRequiredAsterisk
            {...commonSettings}
          />
        ) : (
          <TextInput {...field} {...commonSettings} />
        );
      }}
    />
  );
};
